/*
 * How typewriter mode moves the page to bring the typing line to the
 * eye-line. Every style is animated here, frame by frame, rather than with
 * scrollTo's smooth behavior: WebKit (the Linux and Mac apps) drops a smooth
 * scroll when the text under it changes, so a move started by a keystroke
 * never ran.
 *
 * Gentle styles: Drift, Patient. Typewriter styles: Spring (with a
 * line-feed catch), Carriage Return. Instant is no
 * animation, and is used whatever the style when the system asks for reduced
 * motion.
 */

export const TYPEWRITER_MOTIONS = [
  'instant', 'drift', 'patient', 'spring', 'carriage',
] as const

export type TypewriterMotion = typeof TYPEWRITER_MOTIONS[number]

export interface MotionMeasure {
  container: HTMLElement
  /** The scroll position that puts the typing line at the eye-line. */
  to: number
  /** How far the page may be off before it moves, in px. */
  threshold: number
  /** The distance from one line to the next, the unit moves are judged in. */
  lineHeight: number
  /** The eye-line, in viewport coordinates. */
  eyeY: number
  /** The typing line's top and bottom, in viewport coordinates. */
  lineTop: number
  lineBottom: number
  /** The part of the scroll area the writer can see, in viewport coordinates. */
  viewTop: number
  viewBottom: number
}

interface MotionOptions {
  style: () => TypewriterMotion
  bail: () => boolean
  /** Where the typing line is, or null when there is nothing to follow. */
  measure: () => MotionMeasure | null
  /** The text column: the Paper Bail spans it, and Carriage Return throws its parent sideways. */
  column: () => HTMLElement | null
}

/** A move longer than this many lines is a return, not a line feed. */
const RETURN_LINES = 1.6
/** How long Patient waits for the typing to stop. */
const PATIENT_PAUSE_MS = 800
/** Patient stops waiting when the line comes within this share of the screen of its top or bottom edge. */
const PATIENT_EDGE_SHARE = 0.15
/** Carriage Return: how far the column is thrown sideways, in px, and for how long. */
const CARRIAGE_THROW = 9
const CARRIAGE_MS = 420

interface Animator {
  /** The scroll position at `now`, and whether the move has finished. */
  step(now: number): { pos: number; done: boolean }
  /** Current speed in px/ms, so a new move can carry on from it. */
  velocity(now: number): number
}

/**
 * A cubic Hermite curve from the current position and speed to rest at the
 * target. Starting from rest it is a smoothstep: it eases in and out.
 */
function curve(from: number, to: number, v0: number, start: number, duration: number): Animator {
  const span = v0 * duration
  const at = (now: number) => Math.min(1, Math.max(0, (now - start) / duration))
  return {
    step(now) {
      const t = at(now)
      const t2 = t * t
      const t3 = t2 * t
      const pos = (2 * t3 - 3 * t2 + 1) * from + (t3 - 2 * t2 + t) * span + (-2 * t3 + 3 * t2) * to
      return { pos, done: t >= 1 }
    },
    velocity(now) {
      const t = at(now)
      if (t >= 1) return 0
      const t2 = t * t
      return ((6 * t2 - 6 * t) * from + (3 * t2 - 4 * t + 1) * span + (-6 * t2 + 6 * t) * to) / duration
    },
  }
}

/**
 * A damped spring pulling toward the target. Below a damping of 1 it
 * overshoots slightly before settling; at 1 it arrives without passing.
 */
function spring(from: number, to: number, v0: number, start: number, omega: number, zeta: number): Animator {
  let x = from
  let v = v0 * 1000 // px/s
  let last = start
  const k = omega * omega
  const c = 2 * zeta * omega
  return {
    step(now) {
      // Fixed small steps keep the spring stable on slow frames.
      let elapsed = Math.min(0.064, Math.max(0, (now - last) / 1000))
      last = now
      while (elapsed > 0) {
        const h = Math.min(elapsed, 1 / 240)
        v += (-k * (x - to) - c * v) * h
        x += v * h
        elapsed -= h
      }
      const done = Math.abs(x - to) < 0.3 && Math.abs(v) < 6
      return { pos: done ? to : x, done }
    },
    velocity: () => v / 1000,
  }
}

export class TypewriterMotionDriver {
  private animator: Animator | null = null
  private container: HTMLElement | null = null
  private frame = 0
  private pending = 0
  /** When the current sideways throw began, or 0 when there is none. */
  private throwStart = 0
  private bailEl: HTMLDivElement | null = null
  private bailTimer = 0
  private reducedMotion = typeof matchMedia === 'function'
    ? matchMedia('(prefers-reduced-motion: reduce)')
    : null

  constructor(private options: MotionOptions) {}

  request() {
    const m = this.options.measure()
    if (!m) return
    const style = this.reducedMotion?.matches ? 'instant' : this.options.style()
    if (Math.abs(m.to - m.container.scrollTop) <= m.threshold) return
    // Patient never moves the page while the writer is typing, whatever
    // brought the line away (a wrap, Enter, a click elsewhere): it waits for a
    // pause, unless the line is about to leave the screen.
    if (style === 'patient' && !this.animator) {
      const margin = (m.viewBottom - m.viewTop) * PATIENT_EDGE_SHARE
      if (m.lineTop >= m.viewTop + margin && m.lineBottom <= m.viewBottom - margin) {
        this.wait()
        return
      }
    }
    this.clearPending()
    this.move(style, m, m.to)
  }

  /** Ends any move in progress and any move Patient is waiting to make. */
  stop() {
    this.clearPending()
    cancelAnimationFrame(this.frame)
    this.frame = 0
    this.animator = null
    this.container = null
    this.endThrow()
  }

  destroy() {
    this.stop()
    clearTimeout(this.bailTimer)
    this.bailEl?.remove()
    this.bailEl = null
  }

  private wait() {
    this.clearPending()
    this.pending = window.setTimeout(() => {
      this.pending = 0
      const m = this.options.measure()
      if (m && Math.abs(m.to - m.container.scrollTop) > m.threshold) this.move('patient', m, m.to)
    }, PATIENT_PAUSE_MS)
  }

  private clearPending() {
    clearTimeout(this.pending)
    this.pending = 0
  }

  private move(style: TypewriterMotion, m: MotionMeasure, target: number) {
    const now = performance.now()
    const el = m.container
    const from = el.scrollTop
    const to = Math.max(0, Math.min(target, el.scrollHeight - el.clientHeight))
    const distance = Math.abs(to - from)
    const isReturn = distance > m.lineHeight * RETURN_LINES
    // A move already under way hands on its speed, so changing course never lurches.
    const v0 = this.animator && this.container === el ? this.animator.velocity(now) : 0
    let next: Animator | null = null

    switch (style) {
      case 'drift':
      case 'patient':
        next = curve(from, to, v0, now, Math.min(650, Math.max(300, 300 + distance * 0.6)))
        break
      case 'spring':
        // Returns arrive without passing; a single line lands with a small catch.
        next = isReturn ? spring(from, to, v0, now, 14, 1) : spring(from, to, v0, now, 22, 0.66)
        break
      case 'carriage':
        next = curve(from, to, v0, now, isReturn ? CARRIAGE_MS : 160)
        // A throw already under way runs its course rather than restarting.
        if (isReturn && !this.throwStart) this.throwStart = now
        break
    }

    if (!next) {
      this.stop()
      el.scrollTop = to
      return
    }
    if (isReturn && style !== 'instant' && this.options.bail()) this.showBail(m)
    this.animator = next
    this.container = el
    if (!this.frame) this.frame = requestAnimationFrame(this.tick)
  }

  private tick = (now: number) => {
    this.frame = 0
    const animator = this.animator
    const el = this.container
    if (!animator || !el) return
    const { pos, done } = animator.step(now)
    el.scrollTop = pos
    let throwing = false
    if (this.throwStart) {
      const t = (now - this.throwStart) / CARRIAGE_MS
      throwing = t < 1
      const shifted = this.options.column()?.parentElement
      if (!throwing) this.endThrow()
      else if (shifted) shifted.style.translate = `${(CARRIAGE_THROW * Math.sin(Math.PI * Math.max(0, t))).toFixed(2)}px 0`
    }
    if (done && !throwing) {
      this.animator = null
      this.container = null
      this.hideBail()
    } else {
      this.frame = requestAnimationFrame(this.tick)
    }
  }

  private endThrow() {
    if (!this.throwStart) return
    this.throwStart = 0
    const shifted = this.options.column()?.parentElement
    if (shifted) shifted.style.translate = ''
  }

  /** Paper Bail: a hairline under the eye-line while a return arrives. */
  private showBail(m: MotionMeasure) {
    const column = this.options.column()
    if (!column) return
    const rect = column.getBoundingClientRect()
    const style = getComputedStyle(column)
    const left = rect.left + parseFloat(style.paddingLeft || '0')
    const right = rect.right - parseFloat(style.paddingRight || '0')
    if (!this.bailEl) {
      this.bailEl = document.createElement('div')
      this.bailEl.className = 'typewriter-bail'
      this.bailEl.setAttribute('aria-hidden', 'true')
      document.body.appendChild(this.bailEl)
    }
    clearTimeout(this.bailTimer)
    Object.assign(this.bailEl.style, {
      top: `${Math.round(m.eyeY + m.lineHeight + 3)}px`,
      left: `${Math.round(left)}px`,
      width: `${Math.max(0, Math.round(right - left))}px`,
    })
    this.bailEl.classList.add('on')
  }

  private hideBail() {
    const el = this.bailEl
    if (!el?.classList.contains('on')) return
    // It stays a moment after the line arrives, then fades.
    clearTimeout(this.bailTimer)
    this.bailTimer = window.setTimeout(() => el.classList.remove('on'), 120)
  }
}
